import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { SearchModal } from "./search-modal";
import { SearchPaletteDesktop } from "./search-palette-desktop";

/**
 * Self-contained search trigger: renders an icon button and owns the modal
 * state. Drop into a page's nav bar `trailingElements` array — no provider or
 * external state required.
 *
 * Responsive: mobile gets the vaul-backed `SearchModal` (swipe-down dismiss),
 * desktop gets the cmdk-backed `SearchPaletteDesktop` with a Cmd+K / Ctrl+K
 * keyboard shortcut. Both share the same data layer.
 */
export function SettingsSearchTrigger() {
  const { t } = useTranslation("settings");
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  // Desktop only: bind ⌘K / Ctrl+K to toggle the palette. Bound here (rather
  // than at the layout level) so the shortcut is only live while a settings
  // page is mounted — avoids stealing the chord from other parts of the app.
  useEffect(() => {
    if (isMobile) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isMobile]);

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        aria-label={t("search.open")}
        title={isMobile ? undefined : t("search.openHint")}
      >
        <Search className="h-5 w-5" />
      </Button>
      {isMobile ? (
        <SearchModal open={open} onOpenChange={setOpen} />
      ) : (
        <SearchPaletteDesktop open={open} onOpenChange={setOpen} />
      )}
    </>
  );
}
